/**
 * Programmatic, mobile-safe scroll-to-section.
 *
 * Why programmatic instead of <a href="#id">?
 * - href anchors on mobile Safari occasionally race with Lenis's touch handler.
 * - Native anchor scroll ignores the fixed nav offset (target lands under the bar).
 * - We also want to place focus in the first input on the waitlist section.
 *
 * Handles lazy-image layout shift: after the initial scroll finishes, we
 * re-measure the target and re-scroll if the delta is >40px. This fixes the
 * common "first cold click lands short" bug where <img loading="lazy">
 * elements below the fold expand the document during the scroll animation.
 *
 * Usage:
 *   import { scrollToSection } from "@/lib/scrollToSection";
 *   <button onClick={() => scrollToSection("waitlist", { focus: "input" })}>...</button>
 */

const NAV_OFFSET_MOBILE = 76;
const NAV_OFFSET_DESKTOP = 88;

function navOffset() {
  if (typeof window === "undefined") return NAV_OFFSET_DESKTOP;
  return window.matchMedia("(min-width: 768px)").matches
    ? NAV_OFFSET_DESKTOP
    : NAV_OFFSET_MOBILE;
}

function scrollTo(target, offset, duration) {
  const lenis = window.__lenis;
  if (lenis && typeof lenis.scrollTo === "function") {
    lenis.scrollTo(target, {
      offset,
      duration,
      easing: (t) => 1 - Math.pow(1 - t, 3),
    });
  } else {
    const y = target.getBoundingClientRect().top + window.scrollY + offset;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
}

export function scrollToSection(id, opts = {}) {
  if (typeof document === "undefined") return;
  const target = typeof id === "string" ? document.getElementById(id) : id;
  if (!target) return;

  const offset = -navOffset();

  scrollTo(target, offset, 1.05);

  // Post-scroll verification. Below-fold images with loading="lazy" may
  // expand their containers as they enter the viewport during the scroll,
  // pushing the section further down the document. If we're > 40px off,
  // re-scroll to the (now stable) position.
  const verifyAt = 1200;
  const verifyTimer = setTimeout(() => {
    const rectTop = target.getBoundingClientRect().top;
    const desiredTop = -offset; // e.g. 76
    if (Math.abs(rectTop - desiredTop) > 40) {
      scrollTo(target, offset, 0.6);
    }
  }, verifyAt);

  if (opts.focus) {
    // Wait for the scroll (and any retry) to settle before focusing so
    // mobile keyboards don't fight the animation.
    setTimeout(() => {
      const el = target.querySelector(opts.focus);
      if (el && typeof el.focus === "function") {
        el.focus({ preventScroll: true });
      }
    }, verifyAt + 700);
  }

  return () => clearTimeout(verifyTimer);
}
